import { usePubNub } from "pubnub-react";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSendSMSMutation } from "../../../api/network/chatApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { SendSMSPayload } from "../../../api/types/Chat";
import svgIcons from "../../../assets/svg/svgIcons";
import { useChatContext } from "../contexts/chat.context";
import { TPubNubConversationMessage } from "../contexts/types/pubnubChat";
import SMSConversationMessage from "./smsConversationMessage";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import PagewideMessageDisplay from "../../../components/pagewideMessageDisplay";

type TSMSConversationProps = {
  wrapperClass?: string;
};

const SMSConversation: FC<TSMSConversationProps> = ({
  wrapperClass = "col-span-8 h-[calc(100vh-160px)]",
}) => {
  const pubnub = usePubNub();
  const { t } = useTranslation("translation", { keyPrefix: "smsSection" });

  const {
    dataSMSUsers,
    selectedSMSUser,
    dataSMSConversation,
    isFetchingSMSConversation,
    refetchSMSConversation,
  } = useChatContext();

  const [conversationMessages, setConversationMessages] = useState<
    TPubNubConversationMessage[]
  >([]);
  const [inputText, setInputText] = useState<string>("");
  const conversationMessagesWrapperRef = React.createRef<HTMLDivElement>();

  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const { getUserACLAccessLevel } = useACL();

  // send sms mutation
  const [sendSMSAPITrigger, { isLoading: isLoadingSendSMS }] =
    useSendSMSMutation();

  // publish a message to the channel
  const sendNewMessage = (message: string) => {
    if (message) {
      //prepare payload
      const payload: SendSMSPayload = {
        members: [selectedSMSUser],
        message: message,
      };
      sendSMSAPITrigger(payload)
        .unwrap()
        .then((data) => {
          setInputText("");
          setTimeout(() => {
            // refetch conversation after 3 seconds
            refetchSMSConversation();
            if (!!data?.message) {
              setTimeout(() => {
                toast.success(t("create_new.toast.send_success"));
              }, 1000 * 3); // simulate delay in toast message to let the refetch happen
            } else {
              toast.error(t("create_new.toast.send_failed"));
            }
          }, 1000 * 3);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("create_new.toast.send_failed")];
          toast.error(errors?.join(" "));
        });
    }
  };

  // on new message, scroll to bottom
  const scrollToBottom = () => {
    if (!conversationMessagesWrapperRef.current) return;
    conversationMessagesWrapperRef.current.scrollTop =
      conversationMessagesWrapperRef.current.scrollHeight;
  };
  useEffect(scrollToBottom, [
    conversationMessages,
    conversationMessagesWrapperRef,
  ]);

  useEffect(() => {
    // scroll to bottom
    scrollToBottom();

    // focus on the message input
    messageInputRef?.current?.focus();

    return () => {};
  }, []);

  if (!!!selectedSMSUser) {
    return (
      <div className={`${wrapperClass} flex items center justify-center`}>
        <div className="text-center text-gray-500">
          Select a user to start messaging
        </div>
      </div>
    );
  }

  const messageInputRef = React.createRef<HTMLInputElement>();

  const composeActions = [
    {
      icon: svgIcons.ChatEmojiIcon,
      title: "Emoji",
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        // blur the button
        e.currentTarget.blur();
        // launch emoji picker
        setShowEmojiPicker(!showEmojiPicker);
      },
    },
  ];

  // append emoji to input text
  const onEmojiSelect = (emoji: any) => {
    setInputText(inputText + emoji.native);
    setShowEmojiPicker(false);
    messageInputRef?.current?.focus();
  };

  const senderMemberName = dataSMSUsers?.results?.find(
    (user) => user.driverid === selectedSMSUser
  )?.description;

  return (
    <div className={`${wrapperClass}`}>
      <div className="w-full h-full flex flex-col border-l border-l-black/10 pl-4">
        {/* Header */}
        <div className="flex items-center h-11 border-b border-b-black/10 text-accent-blue-dark font-bold text-lg px-3 py-2">
          {senderMemberName}
          <span className="flex-grow"></span>
        </div>

        {/* Messages */}
        <div
          ref={conversationMessagesWrapperRef}
          className="flex flex-col gap-3 flex-grow overflow-auto bg-white mt-2"
        >
          {/* {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && <pre className="absolute text-[8px] pointer-events-none touch-none bottom-0 z-10">{JSON.stringify(conversationMessages, null, 2)}</pre>} */}
          {dataSMSConversation?.results.length > 0 ? (
            <>
              {[...dataSMSConversation?.results]
                // because the backend sends messages in latest-first order
                .sort((a, b) => a.timestampms - b.timestampms)
                .map((message, index) => {
                  return (
                    <SMSConversationMessage
                      key={`message-${index}`}
                      message={message}
                    />
                  );
                })}
            </>
          ) : (
            <PagewideMessageDisplay
              title="Start a conversation"
              description="Type a message to begin the conversation"
            />
          )}
          <div className="w-full h-8 flex-shrink-0" />
        </div>

        {/* Footer - Message Input, Submit and More Buttons */}
        <div className={`flex relative`}>
          {/* Message Input and Submit */}
          <span
            className={`relative flex-grow${showEmojiPicker ? " z-modal" : ""}`}
          >
            <input
              ref={messageInputRef}
              type="text"
              className="w-full text-sm border border-black/10 rounded-lg ps-4 pe-12 h-14 enabled:outline-accent-blue-dark"
              // placeholder={t("message_input_placeholder")}
              placeholder={
                getUserACLAccessLevel(ACL_NAMES.ACL_MESSAGING_SEND_MSG) <=
                ROLE_ACCESS_LEVELS.READ
                  ? t("no_permission_placeholder")
                  : t("message_input_placeholder")
              }
              value={inputText}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                sendNewMessage(inputText);
              }}
              onChange={(e) => setInputText(e.target.value)}
              onClick={() => setShowEmojiPicker(false)}
              disabled={
                isLoadingSendSMS ||
                getUserACLAccessLevel(ACL_NAMES.ACL_MESSAGING_SEND_MSG) <=
                  ROLE_ACCESS_LEVELS.READ
              }
            />

            <button
              className={
                `absolute right-2 top-1/2 -translate-y-1/2 size-9 pl-1 flex items-center justify-center group transition-all ` +
                `bg-white enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark rounded-lg`
              }
              onClick={(e) => {
                e.preventDefault();
                sendNewMessage(inputText);
              }}
              disabled={isLoadingSendSMS}
            >
              <img
                className={`size-8 transition-all group-enabled:group-focus:grayscale-0 group-enabled:group-focus:brightness-0 group-enabled:group-focus:invert group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert${
                  !!isFetchingSMSConversation || isLoadingSendSMS
                    ? " grayscale opacity-50"
                    : ""
                }`}
                src={svgIcons.SendChatMessageIcon}
                alt="send"
              />
            </button>
          </span>

          {/* More Buttons */}
          {/* <input type="file" className="absolute bottom-full" ref={fileInputRef} /> */}
          {/* 11/14/2024 disabled as emojis are not supported in database */}
          {/* <div className={`flex items-center gap-2 ml-4 relative${!!isFetchingSMSConversation || isLoadingSendSMS ? ' grayscale opacity-50 pointer-events-none touch-none' : ''}`}>
            {composeActions.map((action, index) => {
              const buttonClassName = `size-9 flex items-center justify-center p-1 group transition-all bg-white enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark rounded-lg`;
              const imageClassName = `size-8 transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert`;
              const imageClassNamePeer = `peer-focus:grayscale-0 peer-focus:brightness-0 peer-focus:invert peer-hover:grayscale-0 peer-hover:brightness-0 peer-hover:invert`;
              return (
                <React.Fragment key={`compose-action-${index}`}>
                  <button
                    className={
                      buttonClassName
                        .replace('bg-white', showEmojiPicker ? 'bg-accent-blue-dark' : 'bg-white')
                        .replace(/enabled\:hover\:/g, showEmojiPicker ? 'enabled:' : 'enabled:hover:') +
                      ` relative${showEmojiPicker ? ' z-popup' : ''}`
                    }
                    onClick={action.action}
                    title={action.title}
                    disabled={isLoadingSendSMS}
                  >
                    <img
                      className={
                        imageClassName
                          .replace(/group\-hover\:/g, showEmojiPicker ? '' : 'group-hover:')
                      }
                      src={action.icon}
                      alt={action.title}
                    />
                  </button>
                </React.Fragment>
              );
            })}
            {showEmojiPicker && (
              <>
                <div className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay" onClick={() => setShowEmojiPicker(!showEmojiPicker)}></div>
                <div className="absolute bottom-full mb-2 right-1 z-popup">
                  <Picker
                    data={emojiMartData}
                    onEmojiSelect={onEmojiSelect}
                  />
                </div>
              </>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default SMSConversation;
